/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import type {
  AccountMeta,
  AccountSignerMeta,
  Address,
  FixedSizeCodec,
  FixedSizeDecoder,
  FixedSizeEncoder,
  Instruction,
  InstructionWithAccounts,
  InstructionWithData,
  ReadonlyAccount,
  ReadonlySignerAccount,
  ReadonlyUint8Array,
  TransactionSigner,
  WritableAccount,
} from "@solana/kit";
import type { ResolvedInstructionAccount } from "@solana/program-client-core";
import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
} from "@solana/kit";
import { getAccountMetaFactory } from "@solana/program-client-core";
import { WHIRLPOOL_PROGRAM_ADDRESS } from "../programs/index.js";

export const DELETE_TOKEN_BADGE_DISCRIMINATOR: ReadonlyUint8Array =
  new Uint8Array([53, 146, 68, 8, 18, 117, 17, 185]);

export function getDeleteTokenBadgeDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    DELETE_TOKEN_BADGE_DISCRIMINATOR,
  );
}

export type DeleteTokenBadgeInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountWhirlpoolsConfig extends string | AccountMeta = string,
  TAccountWhirlpoolsConfigExtension extends string | AccountMeta = string,
  TAccountTokenBadgeAuthority extends string | AccountMeta = string,
  TAccountTokenMint extends string | AccountMeta = string,
  TAccountTokenBadge extends string | AccountMeta = string,
  TAccountReceiver extends string | AccountMeta = string,
  TRemainingAccounts extends readonly AccountMeta[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountWhirlpoolsConfig extends string
        ? ReadonlyAccount<TAccountWhirlpoolsConfig>
        : TAccountWhirlpoolsConfig,
      TAccountWhirlpoolsConfigExtension extends string
        ? ReadonlyAccount<TAccountWhirlpoolsConfigExtension>
        : TAccountWhirlpoolsConfigExtension,
      TAccountTokenBadgeAuthority extends string
        ? ReadonlySignerAccount<TAccountTokenBadgeAuthority> &
            AccountSignerMeta<TAccountTokenBadgeAuthority>
        : TAccountTokenBadgeAuthority,
      TAccountTokenMint extends string
        ? ReadonlyAccount<TAccountTokenMint>
        : TAccountTokenMint,
      TAccountTokenBadge extends string
        ? WritableAccount<TAccountTokenBadge>
        : TAccountTokenBadge,
      TAccountReceiver extends string
        ? WritableAccount<TAccountReceiver>
        : TAccountReceiver,
      ...TRemainingAccounts,
    ]
  >;

export interface DeleteTokenBadgeInstructionData {
  discriminator: ReadonlyUint8Array;
}

export type DeleteTokenBadgeInstructionDataArgs = {};

export function getDeleteTokenBadgeInstructionDataEncoder(): FixedSizeEncoder<DeleteTokenBadgeInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({ ...value, discriminator: DELETE_TOKEN_BADGE_DISCRIMINATOR }),
  );
}

export function getDeleteTokenBadgeInstructionDataDecoder(): FixedSizeDecoder<DeleteTokenBadgeInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getDeleteTokenBadgeInstructionDataCodec(): FixedSizeCodec<
  DeleteTokenBadgeInstructionDataArgs,
  DeleteTokenBadgeInstructionData
> {
  return combineCodec(
    getDeleteTokenBadgeInstructionDataEncoder(),
    getDeleteTokenBadgeInstructionDataDecoder(),
  );
}

export interface DeleteTokenBadgeInput<
  TAccountWhirlpoolsConfig extends string = string,
  TAccountWhirlpoolsConfigExtension extends string = string,
  TAccountTokenBadgeAuthority extends string = string,
  TAccountTokenMint extends string = string,
  TAccountTokenBadge extends string = string,
  TAccountReceiver extends string = string,
> {
  whirlpoolsConfig: Address<TAccountWhirlpoolsConfig>;
  whirlpoolsConfigExtension: Address<TAccountWhirlpoolsConfigExtension>;
  tokenBadgeAuthority: TransactionSigner<TAccountTokenBadgeAuthority>;
  tokenMint: Address<TAccountTokenMint>;
  tokenBadge: Address<TAccountTokenBadge>;
  receiver: Address<TAccountReceiver>;
}

export function getDeleteTokenBadgeInstruction<
  TAccountWhirlpoolsConfig extends string,
  TAccountWhirlpoolsConfigExtension extends string,
  TAccountTokenBadgeAuthority extends string,
  TAccountTokenMint extends string,
  TAccountTokenBadge extends string,
  TAccountReceiver extends string,
  TProgramAddress extends Address = typeof WHIRLPOOL_PROGRAM_ADDRESS,
>(
  input: DeleteTokenBadgeInput<
    TAccountWhirlpoolsConfig,
    TAccountWhirlpoolsConfigExtension,
    TAccountTokenBadgeAuthority,
    TAccountTokenMint,
    TAccountTokenBadge,
    TAccountReceiver
  >,
  config?: { programAddress?: TProgramAddress },
): DeleteTokenBadgeInstruction<
  TProgramAddress,
  TAccountWhirlpoolsConfig,
  TAccountWhirlpoolsConfigExtension,
  TAccountTokenBadgeAuthority,
  TAccountTokenMint,
  TAccountTokenBadge,
  TAccountReceiver
> {
  // Program address.
  const programAddress = config?.programAddress ?? WHIRLPOOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    whirlpoolsConfig: {
      value: input.whirlpoolsConfig ?? null,
      isWritable: false,
    },
    whirlpoolsConfigExtension: {
      value: input.whirlpoolsConfigExtension ?? null,
      isWritable: false,
    },
    tokenBadgeAuthority: {
      value: input.tokenBadgeAuthority ?? null,
      isWritable: false,
    },
    tokenMint: { value: input.tokenMint ?? null, isWritable: false },
    tokenBadge: { value: input.tokenBadge ?? null, isWritable: true },
    receiver: { value: input.receiver ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("whirlpoolsConfig", accounts.whirlpoolsConfig),
      getAccountMeta(
        "whirlpoolsConfigExtension",
        accounts.whirlpoolsConfigExtension,
      ),
      getAccountMeta("tokenBadgeAuthority", accounts.tokenBadgeAuthority),
      getAccountMeta("tokenMint", accounts.tokenMint),
      getAccountMeta("tokenBadge", accounts.tokenBadge),
      getAccountMeta("receiver", accounts.receiver),
    ],
    data: getDeleteTokenBadgeInstructionDataEncoder().encode({}),
    programAddress,
  } as DeleteTokenBadgeInstruction<
    TProgramAddress,
    TAccountWhirlpoolsConfig,
    TAccountWhirlpoolsConfigExtension,
    TAccountTokenBadgeAuthority,
    TAccountTokenMint,
    TAccountTokenBadge,
    TAccountReceiver
  >);
}

export interface ParsedDeleteTokenBadgeInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> {
  programAddress: Address<TProgram>;
  accounts: {
    whirlpoolsConfig: TAccountMetas[0];
    whirlpoolsConfigExtension: TAccountMetas[1];
    tokenBadgeAuthority: TAccountMetas[2];
    tokenMint: TAccountMetas[3];
    tokenBadge: TAccountMetas[4];
    receiver: TAccountMetas[5];
  };
  data: DeleteTokenBadgeInstructionData;
}

export function parseDeleteTokenBadgeInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedDeleteTokenBadgeInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 6) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 6,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      whirlpoolsConfig: getNextAccount(),
      whirlpoolsConfigExtension: getNextAccount(),
      tokenBadgeAuthority: getNextAccount(),
      tokenMint: getNextAccount(),
      tokenBadge: getNextAccount(),
      receiver: getNextAccount(),
    },
    data: getDeleteTokenBadgeInstructionDataDecoder().decode(instruction.data),
  };
}
